'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/context/AuthContext';
import { queryKeys } from '@/lib/queries/queryKeys';
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { needsFromBody, type NeedsBody, type RegistrationNeeds } from '@/lib/registration/client';
import type { QuestionAnswer } from '@/lib/tickets/questions';

/** What an enrollment's ticket still needs of its owner; the form's needs plus where they belong. */
export interface EnrollmentDetails extends RegistrationNeeds {
  contentId: string;
  ticketId: string | null;
}

/** null = nothing is missing. */
export type DetailsData = EnrollmentDetails | null;

export interface SaveDetailsResult {
  ok: boolean;
  stillMissing: boolean;
  /** Set on a refusal about the answers (ANSWERS_INVALID). */
  answerErrors?: Record<string, string>;
}

type DetailsBody = ({ missing: false } | ({ missing: true; contentId: string; ticketId: string | null } & NeedsBody));

/** Complete only on an explicit `missing: false`; anything malformed throws (fail closed). */
function toData(body: DetailsBody | null | undefined): DetailsData {
  if (body && body.missing === false) return null;
  if (!body || body.missing !== true) throw new Error('enrollment details: malformed body');
  return { ...needsFromBody(body), contentId: body.contentId, ticketId: body.ticketId };
}

async function fetchDetails(enrollmentId: string): Promise<DetailsData> {
  const response = await fetchWithAuthRetry(`/api/enrollments/${encodeURIComponent(enrollmentId)}/details`);
  if (!response.ok) throw new Error(`enrollment details ${response.status}`);
  return toData((await response.json()) as DetailsBody);
}

export function useEnrollmentDetails(enrollmentId: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: queryKeys.details.enrollment(enrollmentId ?? ''),
    queryFn: () => fetchDetails(enrollmentId as string),
    enabled: !!enrollmentId,
  });

  const save = useCallback(async (answers: QuestionAnswer[]): Promise<SaveDetailsResult> => {
    if (!enrollmentId) return { ok: false, stillMissing: true };
    let response: Response;
    try {
      response = await fetchWithAuthRetry(`/api/enrollments/${encodeURIComponent(enrollmentId)}/details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
    } catch {
      return { ok: false, stillMissing: true };
    }
    const body = await response.json().catch(() => null);
    if (!response.ok || !body) {
      return {
        ok: false,
        stillMissing: true,
        ...(body?.errorCode === 'ANSWERS_INVALID' && body.answerErrors ? { answerErrors: body.answerErrors } : {}),
      };
    }
    let data: DetailsData;
    try {
      data = toData(body as DetailsBody);
    } catch {
      return { ok: false, stillMissing: true };
    }
    queryClient.setQueryData(queryKeys.details.enrollment(enrollmentId), data);
    // The profile and answers just changed: whatever shows them, or a gate
    // built from them (dashboard cards, certificate readiness), is stale.
    // Only the details refetch is awaited; slower dependants must not hold the form busy.
    if (userId) void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all(userId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.profile.certificateStatusesAll });
    await queryClient.invalidateQueries({ queryKey: queryKeys.details.all });
    return { ok: true, stillMissing: data !== null };
  }, [enrollmentId, userId, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: query.refetch,
    save,
  };
}
